import React, { useState, ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Lightbulb, Search, TrendingUp, TrendingDown, Minus, Plus, Check,
  Loader2, Info, RefreshCw, Trash2, X
} from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import AiCostBadge from '../ai/AiCostBadge';

interface KeywordSuggestion {
  keyword: string;
  searchVolume: number;
  difficulty: number;
  cpc: number;
  trend: 'up' | 'down' | 'stable';
  intent: 'informational' | 'transactional' | 'navigational' | 'commercial';
  competition: 'low' | 'medium' | 'high';
  opportunityScore?: number;
}

interface ExistingKeyword {
  keyword: string;
  search_volume?: number;
}

interface KeywordSuggestionsProps {
  suggestions: KeywordSuggestion[];
  onAddKeywords: (keywords: string[]) => void;
  onGenerateSuggestions: (seed: string) => void;
  onDeleteSuggestions?: (keywords: string[]) => void;
  loading?: boolean;
  seedKeyword?: string;
  existingKeywords?: ExistingKeyword[];
}

// Helper to safely get keyword string from potentially corrupted data
const getKeywordString = (kw: any): string => {
  if (typeof kw === 'string') return kw;
  if (kw && typeof kw === 'object') {
    return kw.value || kw.keyword || kw.text || kw.name || JSON.stringify(kw);
  }
  return String(kw || '');
};

const INTENT_COLORS: Record<string, string> = {
  informational: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  transactional: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  navigational: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  commercial: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
};

const INTENT_LABELS: Record<string, string> = {
  informational: 'Informacional',
  transactional: 'Transacional',
  navigational: 'Navegacional',
  commercial: 'Comercial'
};

const KeywordSuggestions: React.FC<KeywordSuggestionsProps> = ({
  suggestions,
  onAddKeywords,
  onGenerateSuggestions,
  onDeleteSuggestions,
  loading = false,
  seedKeyword = '',
  existingKeywords = []
}) => {
  const { t } = useTranslation();
  const [seed, setSeed] = useState(seedKeyword);
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'volume' | 'difficulty' | 'cpc' | 'opportunity'>('opportunity');
  const [filterIntent, setFilterIntent] = useState<string | null>(null);

  // Filter out invalid suggestions (like [object Object])
  const validSuggestions = useMemo(() => {
    return suggestions.filter(s =>
      s.keyword &&
      typeof s.keyword === 'string' &&
      !s.keyword.includes('[object Object]') &&
      s.keyword.trim() !== ''
    );
  }, [suggestions]);

  // Handle generate - requires user to type something
  const handleGenerate = () => {
    if (seed.trim()) {
      setSelectedKeywords(new Set());
      onGenerateSuggestions(seed.trim());
    }
  };

  // Check if we can generate
  const canGenerate = seed.trim().length > 0;

  const toggleKeyword = (keyword: string) => {
    const newSelected = new Set(selectedKeywords);
    if (newSelected.has(keyword)) {
      newSelected.delete(keyword);
    } else {
      newSelected.add(keyword);
    }
    setSelectedKeywords(newSelected);
  };

  const selectAll = () => {
    const allKeywords = filteredSuggestions.map(s => s.keyword);
    setSelectedKeywords(new Set(allKeywords));
  };

  const clearSelection = () => {
    setSelectedKeywords(new Set());
  };

  const handleAddSelected = () => {
    onAddKeywords(Array.from(selectedKeywords));
    setSelectedKeywords(new Set());
  };

  const handleDeleteSelected = () => {
    if (onDeleteSuggestions && selectedKeywords.size > 0) {
      onDeleteSuggestions(Array.from(selectedKeywords));
      setSelectedKeywords(new Set());
    }
  };

  const handleDeleteSingle = (keyword: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteSuggestions) {
      onDeleteSuggestions([keyword]);
      selectedKeywords.delete(keyword);
      setSelectedKeywords(new Set(selectedKeywords));
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty <= 30) return 'text-green-500';
    if (difficulty <= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getCompetitionBadge = (competition: string) => {
    const colors: Record<string, string> = {
      low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    const labels: Record<string, string> = {
      low: t('keywords.competitionLevel.low'),
      medium: t('keywords.competitionLevel.medium'),
      high: t('keywords.competitionLevel.high')
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${colors[competition] || colors.medium}`}>
        {labels[competition] || competition}
      </span>
    );
  };

  const formatVolume = (volume: number) => {
    if (!volume || isNaN(volume)) return '0';
    if (volume >= 1000000) return `${(volume / 1000000).toFixed(1)}M`;
    if (volume >= 1000) return `${(volume / 1000).toFixed(1)}K`;
    return volume.toString();
  };

  // Filter and sort suggestions
  const filteredSuggestions = useMemo(() => {
    return validSuggestions
      .filter(s => !filterIntent || s.intent === filterIntent)
      .sort((a, b) => {
        switch (sortBy) {
          case 'volume':
            return (b.searchVolume || 0) - (a.searchVolume || 0);
          case 'difficulty':
            return (a.difficulty || 0) - (b.difficulty || 0);
          case 'cpc':
            return (b.cpc || 0) - (a.cpc || 0);
          case 'opportunity':
          default:
            return (b.opportunityScore || 0) - (a.opportunityScore || 0);
        }
      });
  }, [validSuggestions, filterIntent, sortBy]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="p-1.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
            <Lightbulb className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('keywords.aiSuggestions', 'Sugestões de IA')}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {selectedKeywords.size > 0 && onDeleteSuggestions && (
            <Button onClick={handleDeleteSelected} size="sm" variant="danger">
              <Trash2 className="h-4 w-4 mr-1" />
              {t('common.delete', 'Excluir')} ({selectedKeywords.size})
            </Button>
          )}
          {selectedKeywords.size > 0 && (
            <Button onClick={handleAddSelected} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              {t('keywords.addSelected', 'Adicionar')} ({selectedKeywords.size})
            </Button>
          )}
        </div>
      </div>

      {/* Search Input */}
      <div className="flex items-center space-x-2 mb-4">
        <div className="flex-1">
          <Input
            placeholder={t('keywords.enterSeedKeyword', 'Digite uma palavra-chave semente (ex: marketing digital)')}
            value={seed}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSeed(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
            onKeyPress={(e) => e.key === 'Enter' && canGenerate && handleGenerate()}
          />
        </div>
        <Button
          onClick={handleGenerate}
          loading={loading}
          disabled={!canGenerate}
        >
          {loading ? (
            t('common.loading', 'Carregando...')
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-1" />
              {t('keywords.generateSuggestions', 'Gerar')}
            </>
          )}
        </Button>
      </div>
      <AiCostBadge feature="ai-generation" action="keyword-suggestions" className="-mt-2 mb-4" />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('common.filter', 'Filtrar')}:
        </span>
        {Object.entries(INTENT_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilterIntent(filterIntent === key ? null : key)}
            className={`px-2 py-1 text-xs rounded-full transition-colors ${
              filterIntent === key
                ? INTENT_COLORS[key]
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {label}
          </button>
        ))}

        <span className="text-gray-300 dark:text-gray-600">|</span>

        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('common.sortBy', 'Ordenar')}:
        </span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'volume' | 'difficulty' | 'cpc' | 'opportunity')}
          className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
        >
          <option value="opportunity">{t('keywords.opportunityScore', 'Oportunidade')}</option>
          <option value="volume">{t('keywords.searchVolume', 'Volume')}</option>
          <option value="difficulty">{t('keywords.difficulty', 'Dificuldade')}</option>
          <option value="cpc">{t('keywords.cpc', 'CPC')}</option>
        </select>
      </div>

      {/* Selection Actions */}
      {filteredSuggestions.length > 0 && (
        <div className="flex items-center justify-between mb-3 text-sm">
          <div className="flex items-center space-x-2">
            <button
              onClick={selectAll}
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              {t('common.selectAll', 'Selecionar todos')}
            </button>
            {selectedKeywords.size > 0 && (
              <>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <button
                  onClick={clearSelection}
                  className="text-gray-600 dark:text-gray-400 hover:underline"
                >
                  {t('common.clear', 'Limpar')} ({selectedKeywords.size})
                </button>
              </>
            )}
          </div>
          <span className="text-gray-500 dark:text-gray-400">
            {filteredSuggestions.length} {t('keywords.suggestions', 'sugestões')}
          </span>
        </div>
      )}

      {/* Suggestions List */}
      {loading ? (
        <div className="space-y-3">
          {/* Skeleton Loading - mais bonito */}
          <div className="flex items-center gap-2 mb-4">
            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('keywords.generatingSuggestions', 'Analisando mercado e gerando sugestoes...')}
            </span>
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded bg-gray-200 dark:bg-gray-700" />
                  <div>
                    <div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                    <div className="flex gap-2">
                      <div className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded-full" />
                      <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
                    </div>
                  </div>
                </div>
                <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j}>
                    <div className="h-3 w-12 bg-gray-200 dark:bg-gray-700 rounded mb-1" />
                    <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="text-center text-sm text-gray-500 dark:text-gray-400 pt-2">
            {t('keywords.aiAnalyzing', 'A IA esta analisando volume de busca, dificuldade e oportunidades...')}
          </div>
        </div>
      ) : filteredSuggestions.length > 0 ? (
        <div className="space-y-2">
          {filteredSuggestions.map((suggestion) => (
            <div
              key={suggestion.keyword}
              onClick={() => toggleKeyword(suggestion.keyword)}
              className={`p-3 rounded-lg border cursor-pointer transition-all group ${
                selectedKeywords.has(suggestion.keyword)
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${
                    selectedKeywords.has(suggestion.keyword)
                      ? 'bg-blue-500 text-white'
                      : 'border border-gray-300 dark:border-gray-600'
                  }`}>
                    {selectedKeywords.has(suggestion.keyword) && (
                      <Check className="h-3 w-3" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {getKeywordString(suggestion.keyword)}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${INTENT_COLORS[suggestion.intent] || 'bg-gray-100 text-gray-800'}`}>
                        {INTENT_LABELS[suggestion.intent] || suggestion.intent}
                      </span>
                      {getCompetitionBadge(suggestion.competition)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {suggestion.opportunityScore !== undefined && (
                    <div className="flex items-center space-x-1 text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Score:</span>
                      <span className={`font-bold ${
                        suggestion.opportunityScore >= 70 ? 'text-green-500' :
                        suggestion.opportunityScore >= 40 ? 'text-yellow-500' :
                        'text-red-500'
                      }`}>
                        {suggestion.opportunityScore}
                      </span>
                    </div>
                  )}
                  {onDeleteSuggestions && (
                    <button
                      onClick={(e) => handleDeleteSingle(suggestion.keyword, e)}
                      className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      title={t('common.delete', 'Excluir')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mt-3 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                    {t('keywords.searchVolume', 'Volume')}
                  </span>
                  <div className="flex items-center space-x-1">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatVolume(suggestion.searchVolume)}
                    </p>
                    {getTrendIcon(suggestion.trend)}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                    {t('keywords.difficulty', 'Dificuldade')}
                  </span>
                  <p className={`font-medium ${getDifficultyColor(suggestion.difficulty || 50)}`}>
                    {suggestion.difficulty || 0}/100
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                    {t('keywords.cpc', 'CPC')}
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white">
                    R$ {(suggestion.cpc || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                    {t('keywords.trend', 'Tendência')}
                  </span>
                  <div className="flex items-center space-x-1">
                    {getTrendIcon(suggestion.trend)}
                    <span className="text-gray-900 dark:text-white capitalize">
                      {t(`keywords.trendDirection.${suggestion.trend}`, suggestion.trend)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Lightbulb className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="mb-2">{t('keywords.noSuggestions', 'Nenhuma sugestão disponível')}</p>
          <p className="text-sm">
            {t('keywords.enterSeedToGenerate', 'Digite uma palavra-chave semente para gerar sugestões')}
          </p>
        </div>
      )}

      {/* Info */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-start space-x-2">
        <Info className="h-4 w-4 text-blue-500 mt-0.5" />
        <p className="text-xs text-blue-700 dark:text-blue-300">
          {t('keywords.suggestionInfo', 'As sugestões são geradas pela IA com base em análise de mercado, volume de busca e competição. O score de oportunidade considera todos esses fatores para identificar as melhores keywords.')}
        </p>
      </div>
    </div>
  );
};

export default KeywordSuggestions;
